import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, Loader2, Search, Plus, Trash2, Calendar, Clock, User, ClipboardList, Info, ChevronDown, ChevronUp, FileText, X, MapPin, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { LabService } from "../../../../../services/api/LaborderService/lab.service";
import { AdminOrderManagementService } from "../../../../../services/api/admin-order.service";
import { firestore as db } from "../../../../../firebase/firebase";
import { collection, doc, setDoc } from "firebase/firestore";
import CancelOrderModal from "../CancelOrderModal";
import AddressSelectionModal from "../../../../common/AddressModal/AddressSelectionModal";

interface LabtestPrepareOrderProps {
  order: { orderId: number; orderNumber: string };
  onBack: () => void;
}

const morningSlots = [
  "06:00 AM - 06:30 AM",
  "06:30 AM - 07:00 AM",
  "07:00 AM - 07:30 AM",
  "07:30 AM - 08:00 AM",
  "08:00 AM - 08:30 AM",
  "08:30 AM - 09:00 AM",
  "09:00 AM - 09:30 AM",
  "09:30 AM - 10:00 AM",
  "10:00 AM - 10:30 AM",
  "10:30 AM - 11:00 AM",
  "11:00 AM - 11:30 AM",
  "11:30 AM - 12:00 PM",
];

const eveningSlots = [
  "12:00 PM - 12:30 PM",
  "12:30 PM - 01:00 PM",
  "01:00 PM - 01:30 PM",
  "01:30 PM - 02:00 PM",
  "02:00 PM - 02:30 PM",
  "02:30 PM - 03:00 PM",
  "03:00 PM - 03:30 PM",
  "03:30 PM - 04:00 PM",
  "04:00 PM - 04:30 PM",
  "04:30 PM - 05:00 PM",
  "05:00 PM - 05:30 PM",
  "05:30 PM - 06:00 PM",
  "06:00 PM - 06:30 PM",
  "06:30 PM - 07:00 PM",
  "07:00 PM - 07:30 PM",
  "07:30 PM - 08:00 PM",
];

interface BookedLabtestItem {
  uniqueKey: string
  id: string;
  testId: string;
  testName: string;
  isPackage: boolean;
  price: number;
  bookingPatientId: string;
}

const LabtestPrepareOrder: React.FC<LabtestPrepareOrderProps> = ({ order, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any | null>(null);

  // Collapsible Panel States
  const [showOrderDetails, setShowOrderDetails] = useState(true);
  const [showPrescriptions, setShowPrescriptions] = useState(false);

  // Modal Open States
  const [showDateModal, setShowDateModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showAddTestModal, setShowAddTestModal] = useState(false);

  // Patient & Family Options
  const [patientOptions, setPatientOptions] = useState<{ id: string; name: string; relation: string; profileImage?: string }[]>([]);

  // Selected Tests State
  const [bookedTests, setBookedTests] = useState<BookedLabtestItem[]>([]);

  // Slot and Date State (Global)
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");

  // Search Labtests States
  const [searchQuery, setSearchQuery] = useState("");
  const [availableTests, setAvailableTests] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isHealthPackage, setIsHealthPackage] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [order.orderId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      // Fetch Order Details
      const details = await LabService.getLabOrderDetails(order.orderId);
      if (details.success && details.data) {
        setOrderDetails(details.data);

        // Fetch Family Members using patientId from order details
        const patientId = details.data.patientId;
        const familyRes = await AdminOrderManagementService.getPatientAndFamilyDetails(patientId);

        const options: any[] = [];
        if (familyRes.success && familyRes.data) {
          const mainPatient = familyRes.data.patientDetails;
          if (mainPatient) {
            options.push({
              id: mainPatient.userId,
              name: mainPatient.name,
              relation: "Self (Patient)",
              profileImage: mainPatient.profileImage,
            });
          }
          if (Array.isArray(familyRes.data.familyMemberDetails)) {
            familyRes.data.familyMemberDetails.forEach((member: any) => {
              options.push({
                id: member.familyMemberId,
                name: member.name,
                relation: member.relation || "Family member",
                profileImage: member.profileImage,
              });
            });
          }
        }
        setPatientOptions(options);

        // Prepopulate booked tests if already mapped in order details
        if (Array.isArray(details.data.labtestItemsDetails) && details.data.labtestItemsDetails.length > 0) {
          const mapped = details.data.labtestItemsDetails.map((item: any, idx: number) => ({
            uniqueKey: `${item.labtestId || idx}_${Date.now()}_${idx}`,
            labTestId: item.labtestId,
            name: item.labtestName,
            description: "",
            price: parseFloat(item.price) || 0,
            bookingPatientId: item.bookedPatientId || details.data.patientId,
          }));
          setBookedTests(mapped);

          // Prepopulate slot & date from first item
          if (details.data.labtestItemsDetails[0]?.date) {
            setSelectedDate(details.data.labtestItemsDetails[0].date.split("T")[0]);
          }
          if (details.data.labtestItemsDetails[0]?.timeSlot) {
            setSelectedSlot(details.data.labtestItemsDetails[0].timeSlot);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load order information.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableLabtests = useCallback(async (query: string, pkg: boolean) => {
    try {
      setSearchLoading(true);
      const res = await LabService.getLabtestListing(1, 15, pkg, "", query);
      if (res.success && res.data) {
        setAvailableTests(res.data.labTestDetails || []);
      }
    } catch (error) {
      console.error("Error searching labtests:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search trigger
  useEffect(() => {
    if (!showAddTestModal) return;
    const timer = setTimeout(() => {
      fetchAvailableLabtests(searchQuery, isHealthPackage);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, isHealthPackage, showAddTestModal, fetchAvailableLabtests]);

  const handleAddTest = (test: any) => {
    const newItem: BookedLabtestItem = {
      uniqueKey: `${test.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      labTestId: test.id,
      name: test.name,
      description: test.description || "",
      price: test.offerPrice || 0,
      bookingPatientId: patientOptions[0]?.id || "", // Default to main patient
    };
    setBookedTests((prev) => [...prev, newItem]);
    toast.success(`Added "${test.name}"`);
  };

  const handleRemoveTest = (uniqueKey: string) => {
    setBookedTests((prev) => prev.filter((t) => t.uniqueKey !== uniqueKey));
  };

  const handleUpdatePrice = (uniqueKey: string, priceVal: number) => {
    setBookedTests((prev) =>
      prev.map((t) => (t.uniqueKey === uniqueKey ? { ...t, price: priceVal } : t))
    );
  };

  const handleUpdatePatient = (uniqueKey: string, patientId: string) => {
    setBookedTests((prev) =>
      prev.map((t) => (t.uniqueKey === uniqueKey ? { ...t, bookingPatientId: patientId } : t))
    );
  };

  const totalPayable = bookedTests.reduce((sum, t) => sum + (t.price || 0), 0);

  const addNotificationInFirebase = async () => {
    if (!orderDetails) return;
    const docRef = doc(
      collection(db, "paymentCompletion"),
      String(order.orderId)
    );
    await setDoc(
      docRef,
      {
        message:
          "Your order has been prepared. You are required to complete payment for further processing.",
        title: "Order Prepared by admin",
        toId: orderDetails?.patientId || "",
        status: "Unread",
        order_number: orderDetails?.orderNumber || "",
        order_date: orderDetails?.orderDate || "",
        isLabtestOrder: true
      },
      { merge: true }
    );
  };

  const handlePrepareSubmit = async () => {
    if (bookedTests.length === 0) {
      toast.error("Please add at least one lab test.");
      return;
    }
    if (!selectedDate) {
      toast.error("Please select a collection date.");
      return;
    }
    if (!selectedSlot) {
      toast.error("Please select a time slot.");
      return;
    }
    if (bookedTests.some((t) => !t.bookingPatientId)) {
      toast.error("All tests must be assigned to a patient.");
      return;
    }

    setIsProcessing(true);
    try {
      const labtestsPayload = bookedTests.map((t) => ({
        labTestId: t.labTestId,
        price: t.price,
        timeSlot: selectedSlot,
        date: new Date(selectedDate).toISOString(),
        bookingPatientId: t.bookingPatientId,
      }));

      const payload = {
        orderId: order.orderId,
        orderTotal: totalPayable,
        labtests: labtestsPayload,
      };

      const res = await LabService.prepareLabtestOrder(payload);
      if (res.success) {
        await addNotificationInFirebase();
        toast.success("Labtest order prepared successfully!");
        onBack();
      } else {
        toast.error(res.message || "Failed to prepare order.");
      }
    } catch (error) {
      console.error("Error preparing order:", error);
      toast.error("Failed to prepare labtest order.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate next 7 days for selection
  const generateDates = () => {
    const dates: { dateString: string; label: string }[] = [];
    const today = new Date();
    for (let i = 0; i < 8; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const iso = date.toISOString().split("T")[0];
      const isToday = i === 0;
      const display = date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      });
      dates.push({
        dateString: iso,
        label: isToday ? "Today" : display,
      });
    }
    return dates;
  };

  const nextDates = generateDates();

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden relative">
      <div className="flex-1 overflow-auto p-5">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700"
        >
          <ChevronLeft size={16} /> Back to Labtest Orders
        </button>

        <div className="flex flex-col lg:flex-row gap-5">
          {/* Main Form Fields */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">

            {/* 1. Collapsible Order Details Block */}
            {orderDetails && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  onClick={() => setShowOrderDetails(!showOrderDetails)}
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                >
                  <span className="uppercase tracking-wider">Approve Order Details ({orderDetails.orderNumber})</span>
                  {showOrderDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showOrderDetails && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-semibold text-slate-600">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Order ID</span>
                        <span className="text-slate-800 font-extrabold text-sm">#{orderDetails.orderNumber}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Order Date</span>
                        <span className="text-slate-800 font-bold">{formatDate(orderDetails.orderDate)}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Ordered By</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          {orderDetails.patientDetails?.patientProfileImage && (
                            <img
                              src={orderDetails.patientDetails.patientProfileImage}
                              alt=""
                              className="w-7 h-7 rounded-full object-cover border border-slate-200"
                            />
                          )}
                          <div>
                            <div className="text-slate-800 font-bold">{orderDetails.patientDetails?.patientName}</div>
                            <div className="text-[0.6rem] text-slate-400 font-semibold">
                              +91 {orderDetails.patientPhoneNumber || orderDetails.patientDetails?.patientMobileNumber}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Clinic Name</span>
                        <span className="text-slate-800 font-bold block">{orderDetails.clinicDetails?.clinicName || "—"}</span>
                        {orderDetails.clinicDetails?.clinicMobileNumber && (
                          <span className="text-[0.6rem] text-slate-400 block font-semibold">
                            +91 {orderDetails.clinicDetails.clinicMobileNumber}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-slate-400 uppercase tracking-wide font-bold">Collection Address</span>
                        <button
                          onClick={() => setShowAddressModal(true)}
                          className="text-[0.6rem] text-emerald-600 hover:text-white bg-emerald-50 hover:bg-emerald-500 rounded px-1.5 py-0.5 border border-emerald-200 font-bold cursor-pointer transition-all shadow-xs"
                        >
                          Edit Address
                        </button>
                      </div>
                      <div className="text-xs text-slate-700 flex items-start gap-1 font-semibold">
                        <MapPin size={13} className="text-rose-500 mt-0.5 flex-shrink-0" />
                        <span>
                          {orderDetails.orderAddress} (Pincode: {orderDetails.orderPincode})
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 2. Collapsible Prescriptions Block */}
            {orderDetails?.labtestPrescriptionDetails && orderDetails.labtestPrescriptionDetails.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  onClick={() => setShowPrescriptions(!showPrescriptions)}
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                >
                  <span className="uppercase tracking-wider">Uploaded Prescriptions</span>
                  {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showPrescriptions && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex flex-col gap-2">
                    {orderDetails.labtestPrescriptionDetails.map((url: string, idx: number) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                        <div className="flex items-center gap-2">
                          <FileText className="text-rose-500 w-6 h-6 flex-shrink-0" />
                          <span className="text-xs font-bold text-slate-800">Prescription Document #{idx + 1}</span>
                        </div>
                        <button
                          onClick={() => window.open(url, "_blank")}
                          className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-slate-105 hover:bg-slate-200 text-slate-700 border border-slate-200 cursor-pointer"
                        >
                          View Document
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 3. Global Date & Slot Selector Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
                <div className="min-w-0">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Collection Date</span>
                  <span className="text-slate-800 font-extrabold text-xs flex items-center gap-1.5 truncate">
                    <Calendar size={14} className="text-emerald-500 flex-shrink-0" />
                    {selectedDate ? new Date(selectedDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Not Selected"}
                  </span>
                </div>
                <button
                  onClick={() => setShowDateModal(true)}
                  className="px-3.5 py-1.5 rounded-lg text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors cursor-pointer flex-shrink-0"
                >
                  Change Date
                </button>
              </div>

              {/* Slot Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
                <div className="min-w-0">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5 font-bold">Collection Time Slot</span>
                  <span className="text-slate-800 font-extrabold text-xs flex items-center gap-1.5 truncate">
                    <Clock size={14} className="text-emerald-500 flex-shrink-0" />
                    {selectedSlot || "Not Selected"}
                  </span>
                </div>
                <button
                  onClick={() => setShowSlotModal(true)}
                  className="px-3.5 py-1.5 rounded-lg text-[11px] font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors cursor-pointer flex-shrink-0"
                >
                  Change Slot
                </button>
              </div>
            </div>

            {/* 4. Booked Tests List (with "+ Add Test" button) */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm min-h-[350px] flex flex-col">
              <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Tests Booked</h3>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setShowAddTestModal(true);
                  }}
                  className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus size={14} /> Add Test
                </button>
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-[3fr_1.2fr_2fr_0.5fr] gap-2 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-100 pb-2 mb-2 pr-2">
                <div>Test / Package Name</div>
                <div>Amount (₹)</div>
                <div>Ordered For</div>
                <div className="text-center">Action</div>
              </div>

              {/* Items Rows */}
              {bookedTests.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-400">
                  <ClipboardList size={32} className="text-slate-350 mb-2 animate-pulse" />
                  <p className="text-xs font-semibold">No labtests selected yet.</p>
                  <p className="text-[10px] mt-0.5">Click <b>"Add Test"</b> to add tests from catalogue.</p>
                </div>
              ) : (
                <div className="space-y-2 flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
                  {bookedTests.map((test) => (
                    <div
                      key={test.uniqueKey}
                      className="grid grid-cols-[3fr_1.2fr_2fr_0.5fr] gap-2 items-center py-2.5 px-2 border border-slate-100 hover:border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition-all rounded-xl"
                    >
                      {/* Test Name */}
                      <div className="min-w-0 pr-1">
                        <p className="text-xs font-bold text-slate-800 truncate" title={test.name}>
                          {test.name}
                        </p>
                        {test.description && (
                          <p className="text-[9px] text-slate-400 truncate mt-0.5" title={test.description}>
                            {test.description}
                          </p>
                        )}
                      </div>

                      {/* Price input */}
                      <div>
                        <input
                          type="number"
                          value={test.price}
                          onChange={(e) => handleUpdatePrice(test.uniqueKey, parseFloat(e.target.value) || 0)}
                          className="w-20 px-2 py-1 text-xs border border-slate-200 rounded-lg outline-none font-bold text-slate-700 bg-white"
                        />
                      </div>

                      {/* Patient Dropdown */}
                      <div>
                        <select
                          value={test.bookingPatientId}
                          onChange={(e) => handleUpdatePatient(test.uniqueKey, e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-slate-200 rounded-lg outline-none font-bold text-slate-700 bg-white"
                        >
                          <option value="">-- Choose Patient --</option>
                          {patientOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name} ({opt.relation})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Delete */}
                      <div className="flex justify-center">
                        <button
                          onClick={() => handleRemoveTest(test.uniqueKey)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors border-none bg-transparent cursor-pointer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Summary Sidebar Panel */}
          <div className="w-full lg:w-[320px] flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm sticky top-5 flex flex-col gap-4">
              <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
                Order Placement
              </h4>

              {orderDetails && (
                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col gap-2">
                  <div>
                    <span className="text-[8px] text-slate-400 font-extrabold uppercase tracking-wide">Pickup Location</span>
                    <p className="text-xs font-bold text-slate-750 mt-0.5 leading-relaxed">
                      {orderDetails.orderAddress}
                    </p>
                    <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Pincode: {orderDetails.orderPincode}</p>
                  </div>
                  {orderDetails.orderNote && (
                    <div className="mt-1.5 pt-1.5 border-t border-slate-200">
                      <span className="text-[8px] text-slate-400 font-extrabold uppercase tracking-wide">Special Notes</span>
                      <p className="text-[10px] text-slate-650 italic leading-relaxed mt-0.5">
                        "{orderDetails.orderNote}"
                      </p>
                    </div>
                  )}
                </div>
              )}

              <hr className="border-slate-150 my-0.5" />

              <div className="flex flex-col gap-3 text-xs font-semibold text-slate-500">
                <div className="flex justify-between">
                  <span>Selected Date</span>
                  <span className="text-slate-800 font-bold">
                    {selectedDate ? new Date(selectedDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Selected Time Slot</span>
                  <span className="text-slate-800 font-bold">{selectedSlot || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tests count</span>
                  <span className="text-slate-800 font-bold">{bookedTests.length} items</span>
                </div>

                <hr className="border-slate-100 my-0.5" />

                <div className="flex justify-between items-center text-sm font-extrabold text-slate-800 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span>Prepared Total</span>
                  <span className="text-lg font-black text-emerald-600">₹{totalPayable}</span>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col gap-2 mt-2">
                <button
                  onClick={handlePrepareSubmit}
                  disabled={
                    isProcessing ||
                    bookedTests.length === 0 ||
                    !selectedDate ||
                    !selectedSlot ||
                    bookedTests.some((t) => !t.bookingPatientId)
                  }
                  className="w-full py-2.5 rounded-xl text-xs font-extrabold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Preparing...
                    </>
                  ) : (
                    "Confirm & Prepare Order"
                  )}
                </button>
                <button
                  onClick={onBack}
                  disabled={isProcessing}
                  className="w-full py-2.5 rounded-xl text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-650 transition-colors border-none cursor-pointer"
                >
                  Go Back
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={isProcessing}
                  className="w-full py-2.5 rounded-xl text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                >
                  <AlertCircle size={14} /> Cancel Order
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SELECT DATE MODAL ==================== */}
      {showDateModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[250000] flex items-center justify-center p-4"
          onClick={() => setShowDateModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md p-5 shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-150 mb-4">
              <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">Select Collection Date</h3>
              <button
                onClick={() => setShowDateModal(false)}
                className="text-slate-450 hover:text-red-500 border-none bg-transparent cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              {nextDates.map((d) => (
                <button
                  key={d.dateString}
                  onClick={() => {
                    setSelectedDate(d.dateString);
                    setShowDateModal(false);
                  }}
                  className={`px-3 py-2.5 rounded-xl text-xs font-bold border text-center transition-all cursor-pointer ${selectedDate === d.dateString
                    ? "bg-emerald-500 border-emerald-500 text-white shadow-sm font-extrabold"
                    : "bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100"
                    }`}
                >
                  {d.label}
                </button>
              ))}
            </div>

            <div className="pt-3 border-t border-slate-100">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1 font-bold">Or select custom date</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setShowDateModal(false);
                }}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none cursor-pointer bg-slate-50"
              />
            </div>
          </div>
        </div>
      )}

      {/* ==================== SELECT SLOT MODAL (GRID CENTERED) ==================== */}
      {showSlotModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[250000] flex items-center justify-center p-4"
          onClick={() => setShowSlotModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl p-5 shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-3 border-b border-slate-150 mb-4">
              <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider">Select Collection Time Slot</h3>
              <button
                onClick={() => setShowSlotModal(false)}
                className="text-slate-450 hover:text-red-500 border-none bg-transparent cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-5 pr-1" style={{ scrollbarWidth: "thin" }}>
              {/* Morning Slots */}
              <div>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-2.5 text-center">
                  Morning Slots (6:00 AM - 11:00 AM)
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-lg mx-auto">
                  {morningSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => {
                        setSelectedSlot(slot);
                        setShowSlotModal(false);
                      }}
                      className={`px-3 py-2 rounded-xl text-[11px] font-bold border transition-colors cursor-pointer text-center ${selectedSlot === slot
                        ? "bg-emerald-500 border-emerald-500 text-white shadow-sm font-extrabold"
                        : "bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100"
                        }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Afternoon Slots */}
              <div>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-2.5 text-center">
                  Afternoon Slots (12:00 PM - 04:00 PM)
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-lg mx-auto">
                  {afternoonSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => {
                        setSelectedSlot(slot);
                        setShowSlotModal(false);
                      }}
                      className={`px-3 py-2 rounded-xl text-[11px] font-bold border transition-colors cursor-pointer text-center ${selectedSlot === slot
                        ? "bg-emerald-500 border-emerald-500 text-white shadow-sm font-extrabold"
                        : "bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100"
                        }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Evening Slots */}
              <div>
                <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-2.5 text-center">
                  Evening Slots (05:00 PM - 07:00 PM)
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-lg mx-auto">
                  {eveningSlots.map((slot) => (
                    <button
                      key={slot}
                      onClick={() => {
                        setSelectedSlot(slot);
                        setShowSlotModal(false);
                      }}
                      className={`px-3 py-2 rounded-xl text-[11px] font-bold border transition-colors cursor-pointer text-center ${selectedSlot === slot
                        ? "bg-emerald-500 border-emerald-500 text-white shadow-sm font-extrabold"
                        : "bg-slate-50 border-slate-200 text-slate-650 hover:bg-slate-100"
                        }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== ADD LABTEST MODAL (SCROLLABLE CATALOGUE) ==================== */}
      {showAddTestModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowAddTestModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white flex-shrink-0">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Add Lab Test</h3>
                <p className="text-[10px] text-slate-400 font-bold mt-0.5">Select tests from catalogue</p>
              </div>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setShowAddTestModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Filters Section */}
            <div className="px-5 py-3.5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50 flex-shrink-0">
              {/* Search input */}
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search available tests by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 bg-white font-semibold"
                />
              </div>

              {/* Package category filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500">Package category:</span>
                <button
                  onClick={() => setIsHealthPackage(!isHealthPackage)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold border transition-all cursor-pointer ${isHealthPackage
                    ? "bg-indigo-50 border-indigo-200 text-indigo-600 font-extrabold"
                    : "bg-slate-100 border-slate-200 text-slate-500"
                    }`}
                >
                  {isHealthPackage ? "Health Packages" : "Individual Tests"}
                </button>
              </div>
            </div>

            {/* Modal List Area */}
            <div className="flex-1 overflow-y-auto p-5 min-h-0" style={{ scrollbarWidth: "thin" }}>
              <div className="border border-slate-150 rounded-xl overflow-hidden h-full flex flex-col bg-white">
                {/* Catalogue Header */}
                <div className="grid grid-cols-[3fr_1.5fr_1.5fr_1fr] gap-2 text-[10px] font-bold text-slate-400 bg-slate-50 px-3 py-2 uppercase border-b border-slate-150 flex-shrink-0">
                  <div>Test Name</div>
                  <div>Sample Type</div>
                  <div>Price (₹)</div>
                  <div className="text-center">Action</div>
                </div>

                {/* Catalogue Scroll Area */}
                {searchLoading ? (
                  <div className="flex items-center justify-center flex-1 py-10">
                    <Loader2 size={24} className="animate-spin text-emerald-500" />
                  </div>
                ) : availableTests.length === 0 ? (
                  <div className="flex items-center justify-center flex-1 py-10">
                    <p className="text-xs text-slate-450 italic">No labtests matches found.</p>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto divide-y divide-slate-100" style={{ scrollbarWidth: "thin" }}>
                    {availableTests.map((t) => (
                      <div
                        key={t.id}
                        className="grid grid-cols-[3fr_1.5fr_1.5fr_1fr] gap-2 items-center px-3 py-3.5 bg-white hover:bg-slate-50/50 transition-colors"
                      >
                        <div className="min-w-0 pr-1">
                          <p className="text-xs font-bold text-slate-800 truncate">{t.name}</p>
                          {t.description && (
                            <p className="text-[9px] text-slate-450 truncate mt-0.5">{t.description}</p>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-550 font-semibold">{t.sampleType || "—"}</div>
                        <div className="text-xs font-bold text-slate-700">₹{t.offerPrice || 0}</div>
                        <div className="flex justify-center">
                          <button
                            onClick={() => handleAddTest(t)}
                            className="p-1 px-3.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-600 rounded-full text-[10px] font-bold cursor-pointer transition-all"
                          >
                            <Plus size={10} className="inline mr-1" /> Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={order.orderId}
        orderNumber={order.orderNumber}
        orderType="labtest"
        onSuccess={onBack}
      />
      {orderDetails && (
        <AddressSelectionModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          patientId={orderDetails.patientDetails?.patientId || ""}
          orderId={order.orderId}
          currentAddress={orderDetails.orderAddress}
          currentPincode={orderDetails.orderPincode}
          currentClinicId={orderDetails.clinicDetails?.clinicId}
          onAddressUpdated={onBack}
        />
      )}
    </div>
  );
};

export default LabtestPrepareOrder;